import { ReactElement } from 'react';

import styled from 'styled-components';

import { Stack } from '@components/stack';
import { Tooltip } from '@components/tooltip';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

import { IconProps } from '../icons';
import { IconSkip } from '../icons/skip';
import { IconTriggerOff } from '../icons/trigger-off';

export type StatusIndicatorVariantKey =
  | 'notStarted'
  | 'inProgress'
  | 'completed'
  | 'skipped'
  | 'halted'
  | 'error';

type StatusIndicatorVariant = {
  [key in StatusIndicatorVariantKey]: {
    tint?: string;
    iconColor: string;
    iconHover?: string;
    icon?: ReactElement<IconProps>;
  };
};

const Colors: StatusIndicatorVariant = {
  notStarted: {
    iconColor: theme.color.buttonIconPositive,
    iconHover: theme.color.buttonIconPositiveHover,
  },
  inProgress: {
    tint: theme.color.orangeTrans10,
    iconColor: theme.color.elementOrange100,
  },
  completed: {
    tint: theme.color.greenTrans10,
    iconColor: theme.color.elementGreen100,
  },
  skipped: {
    tint: theme.color.uiTrans10,
    iconColor: theme.color.elementPurple50,
    icon: <IconSkip />,
  },
  halted: {
    tint: theme.color.uiTrans10,
    iconColor: theme.color.elementPurple50,
    icon: <IconTriggerOff />,
  },
  error: {
    tint: theme.color.redTrans10,
    iconColor: theme.color.elementRed100,
  },
};

export type StatusIndicatorProps = {
  variant: keyof typeof Colors;
  ratio?: number;
  /** Ignored by `skipped` and `halted`, which always show their own icon. */
  icon?: ReactElement<IconProps>;
  /** Also the accessible name. Defaults to the variant's state. */
  label?: string;
};

const STROKE_WIDTH = 6;

const CircularProgressbar = (props: {
  ratio: number;
  variant: keyof typeof Colors;
  children?: React.ReactNode;
}) => {
  const { ratio, variant, children } = props;

  const radius = 50 - STROKE_WIDTH / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <div style={{ position: 'relative' }}>
      <CircularProgressbarInner
        viewBox="0 0 100 100"
        width={pxToRem(size.button)}
        height={pxToRem(size.button)}
      >
        <circle
          cx="50"
          cy="50"
          r={radius}
          stroke={theme.color.border20}
          strokeWidth={STROKE_WIDTH}
          fill="none"
        />
        <circle
          cx="50"
          cy="50"
          r={radius}
          stroke={Colors[variant].iconColor}
          strokeWidth={STROKE_WIDTH}
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={circumference * (1 - ratio)}
          strokeLinecap="round"
          fill="none"
        />
      </CircularProgressbarInner>
      {children}
    </div>
  );
};

type StatusIndicatorPropsWrapper = {
  variant: keyof typeof Colors;
};

const CircularProgressbarInner = styled.svg`
  transform: rotate(-90deg);
  position: absolute;
`;

// Opaque base under the tint, so overlapping indicators don't show through each other.
const StatusIndicatorWrapper = styled.div<StatusIndicatorPropsWrapper>(({ variant }) => {
  const { tint, iconColor, iconHover } = Colors[variant];

  return {
    backgroundColor: theme.color.backgroundLightDark,
    backgroundImage: tint ? `linear-gradient(${tint}, ${tint})` : undefined,
    borderRadius: '50%',
    width: pxToRem(size.button),
    height: pxToRem(size.button),
    color: iconColor,
    transition: transitionStandard('background-color', 'color'),
    '&:hover': {
      backgroundColor: theme.color.backgroundLightDarkHover,
      color: iconHover ?? iconColor,
    },
  };
});

type ButtonSizeProps = {
  button: number;
  icon: number;
};

const size: ButtonSizeProps = {
  button: 32,
  icon: 16,
};

export const StatusIndicator = (props: StatusIndicatorProps) => {
  const { variant, ratio = 0 } = props;
  const { t } = useScopedTranslate(locale);
  const label = props.label ?? t(variant);
  const icon = Colors[variant].icon ?? props.icon;

  return (
    <Tooltip content={label}>
      <StatusIndicatorWrapper variant={variant} role="img" aria-label={label}>
        <CircularProgressbar variant={variant} ratio={ratio}>
          <Stack align="center" justify="center" style={{ height: pxToRem(size.button) }}>
            {icon && <icon.type {...icon.props} size={size.icon} />}
          </Stack>
        </CircularProgressbar>
      </StatusIndicatorWrapper>
    </Tooltip>
  );
};

export const StatusIndicatorGroup = styled.div({
  display: 'flex',
  isolation: 'isolate',
  '> *': {
    position: 'relative',
  },
  '> *:not(:last-child)': {
    marginRight: pxToRem(-6),
  },
});

const locale = {
  en: {
    notStarted: 'Not started',
    inProgress: 'In progress',
    completed: 'Completed',
    skipped: 'Skipped',
    halted: 'Halted',
    error: 'Error',
  },
};
